import React, { ReactNode } from 'react';
import {
  Select,
  MenuItem,
  InputLabel,
  Typography,
  FormControl,
  SxProps,
  Box,
  useTheme
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import hexToRgba from 'hex-to-rgba';
import { Loading } from 'components/Loading';

type DropdownProps = {
  id?: string;
  label?: string;
  options: { value: string; label: string | ReactNode; disabled?: boolean }[];
  value: string;
  onChange: (value: any) => void;
  helperText?: string;
  placeholder?: string;
  disabled?: boolean;
  menuContainer?: SxProps;
  isLoading?: boolean;
  endMenuComponent?: ReactNode;
  error?: boolean;
  inputProps?: SxProps;
  inputLabelProps?: SxProps;
};

const dropdownStyle: SxProps = {
  height: 56,
  '&.Mui-disabled': {
    color: 'gray', // change text color
    '.MuiOutlinedInput-notchedOutline': {
      borderColor: 'customGrey.dark' // change border color
    }
  }
};

const endMenuContainer: SxProps = {
  position: 'sticky',
  bottom: 0,
  backgroundColor: 'white',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: '100%'
};

const inputStyle: SxProps = {
  '& .MuiOutlinedInput-root.Mui-disabled': {
    root: {
      border: '6px solid #909090 !important'
    }
  }
};

const menuLabelStyle: SxProps = {
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  fontWeight: '400'
};

const menuItemContainer = (primaryColor: string): SxProps => ({
  ':hover': {
    backgroundColor: hexToRgba(primaryColor, 0.1)
  },
  textOverflow: 'ellipsis',
  overflow: 'hidden'
});

const DropDownLoading = () => (
  <Box p={2}>
    <Loading size={20} />
  </Box>
);

/**
 * @deprecated use new DropDown in common/ui
 */
export const Dropdown: React.FC<DropdownProps> = ({
  id,
  label,
  options,
  value,
  onChange,
  helperText,
  placeholder = '',
  disabled = false,
  menuContainer,
  isLoading,
  endMenuComponent,
  error,
  inputProps,
  inputLabelProps
}) => {
  const { palette } = useTheme();

  return (
    <Box sx={{ width: '100%' }}>
      {label && (
        <InputLabel
          id={id}
          sx={{
            color: 'customGrey.dark',
            fontWeight: 'bold',
            fontSize: 14,
            marginBottom: 1,
            ...inputLabelProps
          }}
        >
          {label}
        </InputLabel>
      )}
      <FormControl fullWidth>
        {value === '' ? (
          <InputLabel
            disableAnimation
            shrink={false}
            focused={false}
            id="item_type_label"
            sx={{
              color: disabled ? 'customGrey.medium' : 'customGrey.dark',
              fontWeight: 'normal'
            }}
          >
            {placeholder}
          </InputLabel>
        ) : null}
        <Select
          error={error}
          labelId={id}
          value={value}
          onChange={(e) => onChange(e.target.value as string)}
          fullWidth
          sx={dropdownStyle}
          disabled={disabled || isLoading}
          variant="outlined"
          inputProps={{
            sx: { ...inputStyle, ...inputProps }
          }}
          IconComponent={isLoading ? DropDownLoading : KeyboardArrowDownIcon}
          MenuProps={{
            PaperProps: {
              sx: {
                borderRadius: '8px',
                border: (theme) => `1px solid ${theme.palette.divider}`,
                ...menuContainer
              }
            },
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'center'
            }
          }}
        >
          {options.map((option) => {
            return (
              <MenuItem
                key={option.value}
                value={option.value}
                disabled={option.disabled}
                sx={menuItemContainer(palette.primary.main)}
              >
                <Typography sx={menuLabelStyle} variant="body2">
                  {option.label}
                </Typography>
              </MenuItem>
            );
          })}

          {endMenuComponent ? (
            <Box sx={endMenuContainer}>{endMenuComponent}</Box>
          ) : null}
        </Select>
        {helperText && (
          <Typography sx={{ mt: 1, color: 'customGrey.medium' }}>
            {helperText}
          </Typography>
        )}
      </FormControl>
    </Box>
  );
};
